import { Component, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { SourceTournament } from '../../../models/sourceTournament';
import { SortConfig, TableSortingService } from '../../../services';
import { TablePaginationService } from '../../../services/shared/table-pagination/table-pagination.service';
import { SortableHeaderComponent } from '../../shared/sortable-header/sortable-header.component';
import { TablePaginationComponent } from '../../shared/table-pagination/table-pagination.component';

@Component({
    selector: 'app-tournament-import-list',
    standalone: true,
    imports: [CommonModule, RouterLink, SortableHeaderComponent, TablePaginationComponent],
    templateUrl: './tournament-import-list.component.html',
    styleUrl: './tournament-import-list.component.scss'
})
export class TournamentImportListComponent {
    @Input() set sourceTournaments(value: SourceTournament[]) {
        this._sourceTournaments.set(value);
    }
    @Input() set filteredSourceTournaments(value: SourceTournament[]) {
        this._filteredSourceTournaments.set(value);
    }

    @Output() openImportModal = new EventEmitter<SourceTournament>();
    @Output() exportSourceTournaments = new EventEmitter<void>();

    // Internal signals
    _sourceTournaments = signal<SourceTournament[]>([]);
    _filteredSourceTournaments = signal<SourceTournament[]>([]);
    sortConfig = signal<SortConfig | null>(null);

    // Pagination signals
    currentPage = signal<number>(1);
    pageSize = signal<number>(10); // Optimized for both 1920x1080 and 1440x900 to ensure pagination is always visible

    // Computed sorted tournaments
    sortedTournaments = computed(() => {
        const tournaments = this._filteredSourceTournaments();
        const sort = this.sortConfig();

        if (!sort) {
            return tournaments;
        }

        return this.tableSortingService.sort(tournaments, sort.key, sort.direction);
    });

    // Computed pagination result
    paginationResult = computed(() => {
        const sorted = this.sortedTournaments();
        const currentPageValue = this.currentPage();
        const pageSizeValue = this.pageSize();

        return this.tablePaginationService.paginate(
            sorted,
            currentPageValue,
            pageSizeValue
        );
    });

    constructor(private tableSortingService: TableSortingService, private tablePaginationService: TablePaginationService) { }

    onOpenImportModal(tournament: SourceTournament): void {
        this.openImportModal.emit(tournament);
    }

    onSort(event: { key: string; direction: 'asc' | 'desc' }) {
        this.sortConfig.set({ key: event.key, direction: event.direction });
    }

    onPageChange(page: number) {
        this.currentPage.set(page);
    }

    onExportSourceTournaments(): void {
        this.exportSourceTournaments.emit();
    }
}
